import styled from 'styled-components';
import { GlobalToken } from 'antd';
import { CustomThemeToken } from '@/theme/theme.utils';

export const InsightsContainer = styled.div<{ $isExpanded?: boolean; theme: { token: GlobalToken } }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  background-color: transparent;
  padding: ${({ theme: { token } }) => token.paddingXS}px;
  overflow-x: visible;
  overflow-y: visible;
  flex: 0 0 auto;
  min-height: 0;
`;

export const SentinelElement = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
  visibility: hidden;
`;

export const StickyHeaderSection = styled.div<{ theme: { token: GlobalToken } }>`
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  background-color: ${({ theme: { token } }) => (token as CustomThemeToken).colorBgElevatedDarker};
  padding-top: ${({ theme: { token } }) => token.marginXL}px;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  will-change: transform;
  contain: layout style paint;
  isolation: isolate;
`;

export const InsightsHeader = styled.div<{ $isExpanded?: boolean; theme: { token: GlobalToken } }>`
  margin-bottom: ${({ theme: { token } }) => token.marginLG}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme: { token } }) => token.marginLG}px;
  padding-top: 0;
  overflow-x: visible;
  min-width: 0;
`;

export const InsightsTitleRow = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginSM}px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
  overflow-x: visible;
`;

export const InsightsTitleGroup = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginSM}px;
  flex-wrap: wrap;
`;

export const InsightsTitle = styled.h2<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSizeHeading4 || 20}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  margin: 0;
  white-space: nowrap;
  overflow: visible;
`;

export const PendingValue = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  white-space: nowrap;
  overflow: visible;
`;

export const PendingValueLabel = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
`;

export const PendingValueChip = styled.span<{ theme: { token: GlobalToken } }>`
  background: ${({ theme: { token } }) => token.colorFillQuaternary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingXS}px;
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
  height: 24px;
  display: inline-flex;
  align-items: center;
`;

export const TabsContainer = styled.div<{ theme: { token: GlobalToken } }>`
  margin-bottom: ${({ theme: { token } }) => token.marginLG}px;
  border-bottom: 1px solid ${({ theme: { token } }) => token.colorBorderSecondary};
  position: relative;
  width: 100%;

  .ant-tabs-nav {
    margin-bottom: 0;

    &::before {
      display: none;
    }
  }

  .ant-tabs-nav-wrap {
    justify-content: flex-start !important;
  }

  .ant-tabs-nav-list {
    justify-content: flex-start !important;
    width: 100%;
  }

  .ant-tabs-tab {
    text-align: left;
    justify-content: flex-start;
    color: ${({ theme: { token } }) => token.colorTextSecondary} !important;

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: ${({ theme: { token } }) => token.colorPrimary} !important;
      }
    }
  }

  .ant-tabs-tab-btn {
    text-align: left;
    color: ${({ theme: { token } }) => token.colorTextSecondary} !important;
  }

  .ant-tabs-ink-bar {
    background: ${({ theme: { token } }) => token.colorPrimary} !important;
    height: 2px !important;
    bottom: -1px !important;
    z-index: 1;
  }
`;

export const TabsHeader = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${({ theme: { token } }) => token.marginLG}px;
  gap: ${({ theme: { token } }) => token.marginMD}px;
  min-height: 40px;
  height: 40px;
`;

export const TabsHeaderFilters = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  flex-shrink: 0;
`;

export const ValueBreakdown = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme: { token } }) => token.marginMD}px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`;

export const ValueBreakdownLabel = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
`;

export const ValueBreakdownItems = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  gap: ${({ theme: { token } }) => token.marginLG}px;
  flex-wrap: wrap;
`;

export const ValueBreakdownItem = styled.div<{
  $type: 'risk' | 'maintenance' | 'energy';
  $showBackground?: boolean;
  theme: { token: GlobalToken };
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  padding: 0 ${({ theme: { token } }) => token.paddingXS}px 0 0;
  height: 28px;
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  background: ${({ $type, $showBackground = true }) => {
    if (!$showBackground) {
      return 'transparent';
    }
    switch ($type) {
      case 'risk':
        return 'linear-gradient(-55.55deg, rgba(255, 91, 85, 0) 32.7%, rgba(255, 91, 85, 0.16) 98.65%), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)';
      case 'maintenance':
        return 'linear-gradient(-56.71deg, rgba(255, 131, 25, 0) 45.03%, rgba(255, 131, 25, 0.14) 90.19%), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)';
      case 'energy':
        return 'linear-gradient(-43.41deg, rgba(82, 196, 26, 0) 36.19%, rgba(82, 196, 26, 0.1) 94.41%), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)';
      default:
        return 'transparent';
    }
  }};
`;

export const ValueBreakdownLine = styled.div<{
  $type: 'risk' | 'maintenance' | 'energy';
  theme: { token: GlobalToken };
}>`
  width: 4px;
  height: 18px;
  background: ${({ $type, theme: { token } }) => {
    switch ($type) {
      case 'risk':
        return token.colorError;
      case 'maintenance':
        return token.colorWarning;
      case 'energy':
        return '#46af16';
      default:
        return token.colorText;
    }
  }};
  border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
  flex-shrink: 0;
`;

export const ValueBreakdownText = styled.span<{
  $type: 'risk' | 'maintenance' | 'energy';
  theme: { token: GlobalToken };
}>`
  color: ${({ $type, theme: { token } }) => {
    switch ($type) {
      case 'risk':
        return token.colorError;
      case 'maintenance':
        return token.colorWarning;
      case 'energy':
        return '#46af16';
      default:
        return token.colorText;
    }
  }};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
`;

export const ValueBreakdownChip = styled.span<{ theme: { token: GlobalToken } }>`
  background: ${({ theme: { token } }) => token.colorFillQuaternary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingXS}px;
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
  height: 20px;
  display: inline-flex;
  align-items: center;
`;

export const InsightsGridWrapper = styled.div<{
  $isExpanded?: boolean;
  $headerHeight?: number;
  $isHeaderStuck?: boolean;
  theme: { token: GlobalToken };
}>`
  position: relative;
  width: 100%;
  padding: ${({ theme: { token } }) => token.paddingXXS}px;
  overflow-x: visible;
  overflow-y: visible;
  flex: 0 0 auto;
  min-height: 80vh;

  /* Custom scrollbar styling - thin and discrete */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0;
    padding: 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    border: none;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
`;

export const InsightsGrid = styled.div<{
  $isExpanded?: boolean;
  theme: { token: GlobalToken };
}>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme: { token } }) => token.marginXS}px;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  overflow-x: visible;
  overflow-y: visible;
  padding-bottom: ${({ theme: { token } }) => token.paddingXL * 2}px;
  align-content: start;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const InsightCardSkeleton = styled.div<{ theme: { token: GlobalToken } }>`
  width: 100%;
  background-color: ${({ theme: { token } }) => token.colorBgElevated};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  overflow: hidden;
`;

export const EmptyState = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { token } }) => token.paddingXL * 2}px ${({ theme: { token } }) => token.padding}px;
  text-align: center;
  grid-column: 1 / -1;
  height: 70vh;
  position: relative;
  width: 100%;
  gap: ${({ theme: { token } }) => token.marginLG}px;
`;

export const EmptyStateBackground = styled.div<{ theme: { token: GlobalToken } }>`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  overflow: hidden;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    max-width: 600px;
    max-height: 500px;
    background-image: radial-gradient(circle, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 22px 22px;
    background-position: center center;
    opacity: 0.6;
    pointer-events: none;
    mask-image: radial-gradient(
      ellipse 70% 60% at 50% 50%,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.9) 50%,
      rgba(0, 0, 0, 0.6) 70%,
      rgba(0, 0, 0, 0.3) 85%,
      rgba(0, 0, 0, 0) 100%
    );
    -webkit-mask-image: radial-gradient(
      ellipse 70% 60% at 50% 50%,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.9) 50%,
      rgba(0, 0, 0, 0.6) 70%,
      rgba(0, 0, 0, 0.3) 85%,
      rgba(0, 0, 0, 0) 100%
    );
  }
`;

export const EmptyStateContent = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme: { token } }) => token.marginMD}px;
  position: relative;
  z-index: 1;
  max-width: 800px;
  width: 100%;
  padding: ${({ theme: { token } }) => token.paddingXL * 2}px;
`;

export const EmptyStateIllustration = styled.div<{ theme: { token: GlobalToken } }>`
  width: 182px;
  height: 128px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme: { token } }) => token.marginMD}px;
  position: relative;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const EmptyStateTitle = styled.h3<{ theme: { token: GlobalToken } }>`
  font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  color: ${({ theme: { token } }) => token.colorText};
  margin: 0;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

export const EmptyStateDescription = styled.p<{ theme: { token: GlobalToken } }>`
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: 400;
  color: ${({ theme: { token } }) => token.colorTextTertiary};
  margin: 0;
  text-align: center;
  max-width: 600px;
  white-space: pre-wrap;
`;

export const EmptyStateButton = styled.button<{ theme: { token: GlobalToken } }>`
  background: ${({ theme: { token } }) => token.colorPrimary};
  border: 1px solid ${({ theme: { token } }) => token.colorPrimary};
  border-radius: ${({ theme: { token } }) => token.borderRadius}px;
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: 400;
  padding: ${({ theme: { token } }) => token.paddingXS}px ${({ theme: { token } }) => token.paddingSM}px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: ${({ theme: { token } }) => token.marginMD}px;

  &:hover {
    background: ${({ theme: { token } }) => token.colorPrimaryHover};
    border-color: ${({ theme: { token } }) => token.colorPrimaryHover};
  }

  &:active {
    background: ${({ theme: { token } }) => token.colorPrimaryActive};
    border-color: ${({ theme: { token } }) => token.colorPrimaryActive};
  }
`;

export const ShowAllButton = styled.button<{ theme: { token: GlobalToken } }>`
  padding: ${({ theme: { token } }) => token.padding}px ${({ theme: { token } }) => token.paddingLG}px;
  background: transparent;
  border: none;
  border-radius: ${({ theme: { token } }) => token.borderRadius}px;
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: 400;
  line-height: 22px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  justify-content: center;
  height: 32px;
  grid-column: 1 / -1;
  justify-self: center;
  margin-top: ${({ theme: { token } }) => token.marginXS}px;

  svg {
    color: ${({ theme: { token } }) => token.colorText};
    fill: ${({ theme: { token } }) => token.colorText};
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${({ theme: { token } }) => token.colorPrimary};
    background: ${({ theme: { token } }) => token.colorFillTertiary};

    svg {
      color: ${({ theme: { token } }) => token.colorPrimary};
      fill: ${({ theme: { token } }) => token.colorPrimary};
    }
  }

  &:active {
    background: ${({ theme: { token } }) => token.colorFillSecondary};
  }
`;

// Keyframe animations for button transitions
const fadeInSlide = `
  @keyframes fadeInSlide {
    from {
      opacity: 0;
      transform: translateX(12px) translateZ(0);
    }
    to {
      opacity: 1;
      transform: translateX(0) translateZ(0);
    }
  }

  @keyframes fadeOutSlide {
    from {
      opacity: 1;
      transform: translateX(0) translateZ(0);
    }
    to {
      opacity: 0;
      transform: translateX(12px) translateZ(0);
    }
  }
`;

export const StickyHeaderButtons = styled.div<{ $isVisible?: boolean; theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  margin-left: auto;
  flex-shrink: 0;
  ${fadeInSlide}

  /* Control pointer events */
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};

  /* Stagger animation for each button with smooth easing - same for enter and exit */
  > * {
    will-change: opacity, transform;
    /* Set initial state explicitly to avoid conflicts */
    ${({ $isVisible }) =>
      $isVisible
        ? `
      opacity: 0;
      transform: translateX(12px) translateZ(0);
      animation: fadeInSlide 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    `
        : `
      opacity: 1;
      transform: translateX(0) translateZ(0);
      animation: fadeOutSlide 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    `}
  }

  > *:nth-child(1) {
    animation-delay: 0s;
  }

  > *:nth-child(2) {
    animation-delay: 0.08s;
  }

  > *:nth-child(3) {
    animation-delay: 0.16s;
  }
`;

export const HeaderButton = styled.button<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  height: 36px;
  padding: 0 ${({ theme: { token } }) => token.paddingSM}px;
  background-color: ${({ theme: { token } }) => token.colorBgContainer};
  border: none;
  border-radius: ${({ theme: { token } }) => token.borderRadius}px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme: { token } }) => token.colorFillQuaternary};
  }

  &:active {
    background-color: ${({ theme: { token } }) => token.colorFillTertiary};
  }
`;

export const ButtonIcon = styled.span<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

export const ButtonText = styled.span<{ theme: { token: GlobalToken } }>`
  font-family: ${({ theme: { token } }) => token.fontFamily};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme: { token } }) => token.colorTextSecondary};
  white-space: nowrap;
`;

export const ThreeDotsButton = styled.button<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: ${({ theme: { token } }) => token.paddingXS}px;
  background-color: ${({ theme: { token } }) => token.colorBgContainer};
  border: none;
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: ${({ theme: { token } }) => token.colorTextSecondary};

  &:hover {
    background-color: ${({ theme: { token } }) => token.colorFillQuaternary};
  }

  &:active {
    background-color: ${({ theme: { token } }) => token.colorFillTertiary};
  }
`;
